import useApiHook from '../../../../hooks/api/useApiHook';
import { useCallback, useEffect } from 'react';
import useAppContext from '../../../../hooks/context/useAppContext';
import { transactionTypeDetermination } from '../../utils/transactionType';

const PURCHASES_API_ROOT = '/purchases';

/**
 * @param {Object} contract Contract
 * @param {Object} product Product
 * @param {number} manualPrice Manual Price
 * @param {number} manualRate Manual Rate
 * @param {PurchaseInfo} purchaseInfo PurchaseInfo
 * @param {Object} purchasePrepare Purchase prepare response
 * @param {boolean} releasing Releasing information
 * @param {boolean} sendNotification send notification to all admins
 * @returns {{purchase, error, loading}} Result and state
 */
export const usePurchase = (contract, product, manualPrice, manualRate, purchaseInfo, purchasePrepare, releasing, sendNotification) => {
  const { postPurchase, data, error, loading } = usePostPurchase();
  const { getUser } = useAppContext();
  const userId = getUser().id;
  const productPriceObj = releasing ? product.salePrice : product.price;
  const purchase = useCallback(async () => {
    const buyTotalsInfo = purchaseInfo.buyTotalsInfo;

    return postPurchase({
      contract: {
        id: contract.id,
      },
      user: {
        id: userId,
      },
      exchangeProduct: {
        id: product.id,
      },
      priceSource: { id: productPriceObj.priceSource },
      idProductPrice: manualPrice ? null : productPriceObj.idProductPrice,
      purchaseIndexType: {
        id: contract.purchaseIndexType.id,
      },
      purchaseType: {
        id: 'STANDARD',
      },
      currency: {
        id: contract.currency.id,
      },
      isSale: false,
      state: contract.state,
      quantity: releasing ? buyTotalsInfo.volumeMWh * -1 : buyTotalsInfo.volumeMWh,
      quantityPercent: releasing ? buyTotalsInfo.percentage * -1 : buyTotalsInfo.percentage,
      range: releasing ? buyTotalsInfo.volumeMW * -1 : buyTotalsInfo.volumeMW,
      price: manualPrice || productPriceObj.price,
      rate: manualRate || purchasePrepare.rate,
      sendNotification: sendNotification,
      quantities: purchaseInfo.buyingProduct.quantities,
      tariffPrices: purchasePrepare.tariffPrices,
      indexValues: purchasePrepare.indexValues,
      transactionType: { id: transactionTypeDetermination(contract.isFix, releasing) },
    });
  }, [
    postPurchase,
    contract,
    userId,
    product,
    manualPrice,
    manualRate,
    purchaseInfo,
    purchasePrepare,
    sendNotification,
    productPriceObj.price,
    productPriceObj.priceSource,
    releasing,
    productPriceObj.idProductPrice,
  ]);
  return {
    purchase,
    purchaseResult: data,
    purchaseError: error,
    purchaseSubmitting: loading,
  };
};

/**
 * @param {Object} contract Contract
 * @param {Object} product Product
 * @param {PurchaseInfo} purchaseInfo PurchaseInfo
 * @param {number} manualPrice Manual Price
 * @param {number} manualRate Manual Rate
 * @param {boolean} releasing Releasing information
 * @returns {{purchase, error, loading}} Result and state
 */
export const usePreparePurchase = (contract, product, purchaseInfo, manualPrice, manualRate, releasing) => {
  const { postPurchasesPrepare, data, error, loading } = usePostPurchasesPrepare();
  const { getUser } = useAppContext();
  const userId = getUser().id;
  const buyTotalsInfo = purchaseInfo.buyTotalsInfo;
  const productPriceObj = releasing ? product.salePrice : product.price;
  useEffect(() => {
    const fetchData = async () => {
      await postPurchasesPrepare({
        contract: {
          id: contract.id,
        },
        exchangeProduct: {
          id: product.id,
        },
        priceSource: { id: productPriceObj.priceSource },
        idProductPrice: manualPrice ? null : productPriceObj.idProductPrice,
        purchaseIndexType: {
          id: contract.purchaseIndexType.id,
        },
        currency: {
          id: contract.currency.id,
        },
        quantity: releasing ? buyTotalsInfo.volumeMWh * -1 : buyTotalsInfo.volumeMWh,
        quantityPercent: releasing ? buyTotalsInfo.percentage * -1 : buyTotalsInfo.percentage,
        range: releasing ? buyTotalsInfo.volumeMW * -1 : buyTotalsInfo.volumeMW,
        price: manualPrice || undefined,
        rate: manualRate || undefined,
      });
    };
    fetchData();
  }, [
    postPurchasesPrepare,
    contract,
    userId,
    product,
    buyTotalsInfo,
    productPriceObj.priceSource,
    manualPrice,
    manualRate,
    releasing,
    productPriceObj.idProductPrice,
  ]);
  return {
    purchasePrepare: data,
    error,
    prepareLoading: loading,
  };
};

const usePostPurchase = () => {
  const { post, data, error, loading } = useApiHook(true);

  const postPurchase = useCallback(
    async (payload) => {
      await post(`${PURCHASES_API_ROOT}`, payload);
    },
    [post],
  );
  return { postPurchase, data, error, loading };
};

const usePostPurchasesPrepare = () => {
  const { post, data, error, loading } = useApiHook(true);

  const postPurchasesPrepare = useCallback(
    async (payload) => {
      await post(`${PURCHASES_API_ROOT}/prepare`, payload);
    },
    [post],
  );
  return { postPurchasesPrepare, data, error, loading };
};
